import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigation, useRoute } from '@react-navigation/native';
import { navigateInShell } from '../../shell/navigation/rootNavigation';
import { useAuthStore } from '../../auth/store/useAuthStore';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { useEmployeesStore, withSelfOwner } from '../../team/store/useEmployeesStore';
import { useBranchPermission } from '../../shell/session/hooks/useBranchPermission';
import { useModulePermission } from '../../shell/session/hooks/useModulePermission';
import { usePlanEntitlements } from '../../shell/providers/PlanEntitlementsProvider';
import { useGlobalFeatureFlags } from '../../shell/providers/GlobalFeatureFlagsProvider';
import { isGlobalFeatureOn } from '../../shell/feature-flags/globalFeatureFlagsService';
import { planAllowsCustomer } from '../../shell/feature-flags/planEntitlementsService';
import { useCashHistoryListFlow } from './hooks/useCashHistoryListFlow';
import {
  computeCashHistoryQueryRange,
  formatTurnOpenDuration,
  mergeCashSummaries,
  deriveCashBranchName,
  deriveCashOutsideSession,
  deriveLastClosedOutsideHistoryRange,
  deriveCashSessionLabels,
  type CashHistoryRangePreset,
} from './cashManagementUtils';
import {
  CASH_OPERATOR_FILTER_ALL,
  type CashDifferenceFilter,
  type CashHistorySort,
  type CashOperatorFilter,
} from './cashList.constants';
import {
  buildCashHistoryOperatorOptions,
  resolveCashOperatorLabel,
} from './cashOperatorLabels';
import { useCashKpis } from './hooks/useCashKpis';
import { useCashDifferences } from './hooks/useCashDifferences';
import { useCashManagementActions } from './hooks/useCashManagementActions';
import { useChecksPortfolio } from './hooks/useChecksPortfolio';
import { useEffectiveBusinessCapabilities } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { subscribeCashSessionChangeSignalDebounced } from '../infra/cashSessionChangeSignal';
import { useOperationalConfirm } from '../../shared/ui/useOperationalConfirm';
import { useSupervisorPinPrompt } from '../../shell/session/useSupervisorPinPrompt';
import { branchHasSupervisorPin } from '../../shell/session/supervisorPinApi';

export type { CashHistoryRangePreset } from './cashManagementUtils';
export {
  computeCashHistoryQueryRange,
  formatTurnOpenDuration,
  mergeCashSummaries,
} from './cashManagementUtils';

export function useCashManagement() {
  const currentBranchId = useSessionStore((state) => state.currentBranchId);
  const branches = useSessionStore((state) => state.branches);
  const user = useAuthStore((state) => state.user);
  const { canView, canEdit, canVoid } = useBranchPermission('Cash');
  const { canView: canViewChecks, canEdit: canEditChecks } = useBranchPermission('Checks');
  const canEndorseChecks = useModulePermission('Checks', 'endorse');
  const canRejectChecks = useModulePermission('Checks', 'reject');
  const { canEdit: canEditExpenses } = useBranchPermission('Expenses');
  const canCorrect = useModulePermission('Cash', 'correct');
  const canMovement = useModulePermission('Cash', 'movement');
  const { entitlements } = usePlanEntitlements();
  const isOwner = entitlements?.is_owner === true;
  const [hasSupervisorPin, setHasSupervisorPin] = useState(false);
  const { flags: globalFeatureFlags } = useGlobalFeatureFlags();
  // Open/close map to canEdit; movement/correct can be tightened via actions jsonb.
  const customerModuleEnabled = useMemo(() => {
    if (!isGlobalFeatureOn(globalFeatureFlags, 'fiado_integrations')) return false;
    return planAllowsCustomer(entitlements);
  }, [globalFeatureFlags, entitlements]);

  const cashOutflowsEnabled = useMemo(
    () => isGlobalFeatureOn(globalFeatureFlags, 'cash_outflows'),
    [globalFeatureFlags]
  );

  const expensesModuleEnabled = useMemo(
    () => isGlobalFeatureOn(globalFeatureFlags, 'expenses_module'),
    [globalFeatureFlags]
  );

  const effectiveCapabilities = useEffectiveBusinessCapabilities();
  const checksModuleEnabled = effectiveCapabilities.has('checks');
  const checksFlow = useChecksPortfolio({
    branchId: currentBranchId ?? null,
    enabled: checksModuleEnabled && canViewChecks,
  });

  const [historyRangePreset, setHistoryRangePreset] = useState<CashHistoryRangePreset>('30d');
  const [customRangeFrom, setCustomRangeFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [customRangeTo, setCustomRangeTo] = useState(() => new Date());
  const [operatorFilter, setOperatorFilter] = useState<CashOperatorFilter>(CASH_OPERATOR_FILTER_ALL);
  const [differenceFilter, setDifferenceFilter] = useState<CashDifferenceFilter>('all');
  const [historySort, setHistorySort] = useState<CashHistorySort>('closed_desc');

  const employeesRaw = useEmployeesStore((s) => s.employees);
  const selfOwner = useEmployeesStore((s) => s.selfOwner);
  const employees = useMemo(() => withSelfOwner(employeesRaw, selfOwner), [employeesRaw, selfOwner]);
  const loadEmployees = useEmployeesStore((s) => s.loadEmployees);

  const reloadHistoryRef = useRef<() => void | Promise<void>>(async () => {});

  const kpis = useCashKpis({ currentBranchId });
  const differences = useCashDifferences({ currentBranchId });

  const historyQueryRange = useMemo(
    () => computeCashHistoryQueryRange(historyRangePreset, customRangeFrom, customRangeTo),
    [historyRangePreset, customRangeFrom, customRangeTo]
  );

  const historyFlow = useCashHistoryListFlow({
    branchId: currentBranchId ?? null,
    fromIso: historyQueryRange.fromIso,
    toIso: historyQueryRange.toIso,
    operatorFilter,
    differenceFilter,
    historySort,
    activeSession: kpis.cashSummary?.activeSession ?? null,
  });

  reloadHistoryRef.current = historyFlow.reloadHistory;

  useEffect(() => {
    return subscribeCashSessionChangeSignalDebounced(() => {
      void reloadHistoryRef.current();
    });
  }, [reloadHistoryRef]);

  const { confirm, ConfirmModal } = useOperationalConfirm();
  const { promptPin, PinModal: SupervisorPinModal } = useSupervisorPinPrompt();
  const navigation = useNavigation();
  const route = useRoute();
  const returnToPos = (route.params as { returnToPos?: boolean } | undefined)?.returnToPos === true;

  const actions = useCashManagementActions({
    currentBranchId,
    userId: user?.id ?? null,
    userEmail: user?.email ?? null,
    canEdit,
    canEditExpenses,
    canView,
    canVoid,
    canMovement,
    canCorrect,
    isOwner,
    cashSummary: kpis.cashSummary,
    applyCashSummaryState: kpis.applyCashSummaryState,
    refreshCashSummary: kpis.refreshCashSummary,
    loadDifferencesFirstPage: differences.loadDifferencesFirstPage,
    reloadHistoryRef,
    expectedCashInDrawer: kpis.expectedCashInDrawer,
    organizationId: entitlements?.organization_id ?? null,
    cashOutflowsEnabled,
    expensesModuleEnabled,
    employees,
    confirm,
    promptSupervisorPin: promptPin,
    onOpenedFromPos: returnToPos ? () => navigateInShell(navigation, 'POS') : undefined,
  });

  const operatorOptions = useMemo(
    () =>
      buildCashHistoryOperatorOptions(
        currentBranchId,
        historyFlow.cashHistory,
        kpis.cashSummary,
        employees,
        user?.id ?? null,
        user?.email
      ),
    [currentBranchId, historyFlow.cashHistory, kpis.cashSummary, employees, user?.id, user?.email]
  );

  useEffect(() => {
    void loadEmployees();
  }, [loadEmployees]);

  useEffect(() => {
    let alive = true;
    if (!currentBranchId) {
      setHasSupervisorPin(false);
      return;
    }
    void branchHasSupervisorPin(currentBranchId)
      .then((ok) => {
        if (alive) setHasSupervisorPin(ok);
      })
      .catch(() => {
        if (alive) setHasSupervisorPin(false);
      });
    return () => {
      alive = false;
    };
  }, [currentBranchId]);

  useEffect(() => {
    setOperatorFilter(CASH_OPERATOR_FILTER_ALL);
  }, [currentBranchId]);

  useEffect(() => {
    setOperatorFilter((prev) => {
      if (prev === CASH_OPERATOR_FILTER_ALL) return prev;
      return operatorOptions.some((o) => o.key === prev) ? prev : CASH_OPERATOR_FILTER_ALL;
    });
  }, [operatorOptions]);

  const refreshLoadData = useCallback(async (options?: { force?: boolean }) => {
    if (!currentBranchId) return;
    await Promise.all([
      kpis.refreshCashSummary(options?.force ? { force: true } : undefined),
      differences.loadDifferencesFirstPage(),
      reloadHistoryRef.current(),
    ]);
  }, [currentBranchId, kpis.refreshCashSummary, differences.loadDifferencesFirstPage, reloadHistoryRef]);

  const loadFn = useCallback(async (opts?: { force?: boolean }) => {
    if (!currentBranchId) return { silent: true };
    if (opts?.force) {
      await Promise.all([
        refreshLoadData({ force: true }),
        actions.refreshActionData({ force: true }),
      ]);
    } else {
      // Refresh de foco: summary + diferencias + historial + movimientos/actividad.
      await Promise.all([
        kpis.loadFn(),
        differences.loadFn(),
        reloadHistoryRef.current(),
        actions.refreshActionData(),
      ]);
    }
  }, [currentBranchId, refreshLoadData, kpis.loadFn, differences.loadFn, actions.refreshActionData]);

  const resolveOperatorLabel = useCallback(
    (authUserId: string | null | undefined) =>
      resolveCashOperatorLabel(authUserId, employees, user?.id ?? null, user?.email),
    [employees, user?.email, user?.id]
  );

  const branchName = useMemo(
    () => deriveCashBranchName(currentBranchId, branches),
    [branches, currentBranchId]
  );

  const cashOutsideSession = deriveCashOutsideSession(kpis.cashSummary);

  const lastClosedOutsideHistoryRange = useMemo(
    () =>
      deriveLastClosedOutsideHistoryRange(
        kpis.cashSummary?.lastClosedSession?.closedAt,
        historyQueryRange
      ),
    [kpis.cashSummary?.lastClosedSession?.closedAt, historyQueryRange]
  );

  const { activeSessionOpenerLabel, lastClosedByLabel, turnOpenDurationLabel } = useMemo(
    () => deriveCashSessionLabels(kpis.cashSummary, employees, user?.id ?? null, user?.email),
    [kpis.cashSummary, employees, user?.email, user?.id]
  );

  const prepareCloseCashModal = useCallback(async () => {
    actions.resetCloseCashForm();
    await kpis.prepareCloseCashModal();
  }, [actions.resetCloseCashForm, kpis.prepareCloseCashModal]);

  return {
    currentBranchId,
    branchName,
    canView,
    canEdit,
    canVoid,
    canShowOutflowVoid: canVoid || isOwner || hasSupervisorPin,
    canMovement,
    canCorrect,
    canEditExpenses,
    customerModuleEnabled,
    cashOutflowsEnabled,
    expensesModuleEnabled,
    checksModuleEnabled,
    canViewChecks,
    canEditChecks,
    canEndorseChecks,
    canRejectChecks,
    checksFlow,
    cashSummary: kpis.cashSummary,
    loading: kpis.loading,
    initialLoading: kpis.initialLoading,
    loadFn,
    openingAmount: actions.openingAmount,
    setOpeningAmount: actions.setOpeningAmount,
    closingAmount: actions.closingAmount,
    setClosingAmount: actions.setClosingAmount,
    closingNote: actions.closingNote,
    setClosingNote: actions.setClosingNote,
    handleOpenCash: actions.handleOpenCash,
    handleCloseCash: actions.handleCloseCash,
    prepareCloseCashModal,
    openingCash: actions.openingCash,
    closingCash: actions.closingCash,
    expectedCashInDrawer: kpis.expectedCashInDrawer,
    cashOutsideSession,
    activeSessionOpenerLabel,
    lastClosedByLabel,
    turnOpenDurationLabel,
    cashHistory: historyFlow.cashHistory,
    historyLoading: historyFlow.historyLoading,
    historyHasMore: historyFlow.historyHasMore,
    historyPageIndex: historyFlow.historyPageIndex,
    historyPageSize: historyFlow.historyPageSize,
    setHistoryPageSize: historyFlow.setHistoryPageSize,
    historyTotalCount: historyFlow.historyTotalCount,
    loadHistoryPreviousPage: historyFlow.loadHistoryPreviousPage,
    loadHistoryNextPage: historyFlow.loadHistoryNextPage,
    goToHistoryPage: historyFlow.goToHistoryPage,
    reloadHistoryFromStart: historyFlow.reloadHistoryFromStart,
    historyError: historyFlow.historyError,
    differencesSummary: differences.differencesSummary,
    historyRangePreset,
    setHistoryRangePreset,
    customRangeFrom,
    setCustomRangeFrom,
    customRangeTo,
    setCustomRangeTo,
    historyQueryRange,
    lastClosedOutsideHistoryRange,
    operatorFilter,
    setOperatorFilter,
    operatorOptions,
    differenceFilter,
    setDifferenceFilter,
    historySort,
    setHistorySort,
    outflowsFlow: actions.outflowsFlow,
    activityFlow: actions.activityFlow,
    resolveOperatorLabel,
    expenseRegisterSuppliers: actions.expenseRegisterSuppliers,
    activeCashSessionInfo: actions.activeCashSessionInfo,
    expenseRegisterSubmit: actions.expenseRegisterSubmit,
    handleOpenRegisterOutflow: actions.handleOpenRegisterOutflow,
    handleOpenRegisterInflow: actions.handleOpenRegisterInflow,
    ConfirmModal,
    SupervisorPinModal,
  };
}
